import { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Megaphone, Send, Plus, Trash2, Tag, Upload, X, Code, Link2 } from 'lucide-react';

export default function NewAnnouncement({ selectedTeam }) {
  const [sending, setSending] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [includeSale, setIncludeSale] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  const [targetChannels, setTargetChannels] = useState(['main']);
  const textareaRef = useRef(null);
  
  // Genel duyuru formu
  const [formData, setFormData] = useState({
    text: '',
    photo_url: '',
    buttons: []
  });
  const [newButton, setNewButton] = useState({ text: '', url: '' });
  const [buttonType, setButtonType] = useState('custom'); // custom or brand

  // Insert HTML tag at cursor position or wrap selected text
  const insertFormatting = (openTag, closeTag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.text;
    const selectedText = text.substring(start, end);
    
    let newText;
    let newCursorPos;
    
    if (selectedText) {
      // Wrap selected text
      newText = text.substring(0, start) + openTag + selectedText + closeTag + text.substring(end);
      newCursorPos = start + openTag.length + selectedText.length + closeTag.length;
    } else {
      // Insert tags at cursor
      newText = text.substring(0, start) + openTag + closeTag + text.substring(end);
      newCursorPos = start + openTag.length;
    }
    
    setFormData({ ...formData, text: newText });
    
    // Focus and set cursor position after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  useEffect(() => {
    if (selectedTeam) {
      loadBrands();
      loadTeamInfo();
    }
  }, [selectedTeam]);

  const loadTeamInfo = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}`);
      setTeamInfo(res.data);
    } catch (err) {
      console.error('Team info load failed', err);
    }
  };

  const loadBrands = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/brands`);
      setBrands(res.data);
    } catch (err) {
      console.error('Brands load failed', err);
    }
  };

  const handleAddButton = () => {
    if (!newButton.text || !newButton.url) {
      toast.error('Buton metni ve URL girin');
      return;
    }
    setFormData({
      ...formData,
      buttons: [...formData.buttons, newButton]
    });
    setNewButton({ text: '', url: '' });
  };

  const handleRemoveButton = (index) => {
    setFormData({
      ...formData,
      buttons: formData.buttons.filter((_, i) => i !== index)
    });
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Sadece resim veya video yükleyebilirsiniz');
      return;
    }

    // Dosya boyutu limiti kaldırıldı - büyük videolar için
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axiosInstance.post('/upload-media', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Backend now returns full URL
      const mediaUrl = res.data.url;
      setFormData({ ...formData, photo_url: mediaUrl });
      setMediaPreview(mediaUrl);
      setMediaType(res.data.type);
      toast.success('Medya yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = () => {
    setFormData({ ...formData, photo_url: '' });
    setMediaPreview(null);
    setMediaType(null);
  };

  const handleAddBrandButton = () => {
    if (!newButton.text) {
      toast.error('Marka seçin');
      return;
    }
    setFormData({
      ...formData,
      buttons: [...formData.buttons, newButton]
    });
    setNewButton({ text: '', url: '' });
    setButtonType('custom');
  };

  const handleSendGeneralAnnouncement = async () => {
    if (!formData.text && !formData.photo_url) {
      toast.error('En az bir metin veya fotoğraf girin');
      return;
    }
    if (!targetChannels || targetChannels.length === 0) {
      toast.error('En az bir hedef kanal seçin');
      return;
    }

    setSending(true);
    try {
      await axiosInstance.post('/telegram/send-announcement', {
        team_id: selectedTeam,
        text: formData.text || undefined,
        photo_url: formData.photo_url || undefined,
        buttons: formData.buttons.length > 0 ? formData.buttons : undefined,
        target_channels: targetChannels
      });
      toast.success('Duyuru başarıyla gönderildi!');
      setFormData({ text: '', photo_url: '', buttons: [] });
      setMediaPreview(null);
      setMediaType(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duyuru gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const toggleTargetChannel = (channel) => {
    setTargetChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const handleSendBrandAnnouncement = async () => {
    if (!selectedBrand) {
      toast.error('Lütfen bir marka seçin');
      return;
    }

    setSending(true);
    try {
      await axiosInstance.post('/telegram/send-brand-announcement', {
        team_id: selectedTeam,
        brand_id: selectedBrand,
        include_sale: includeSale
      });
      toast.success('Marka duyurusu gönderildi!');
      setSelectedBrand(null);
      setIncludeSale(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duyuru gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <Megaphone className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Duyuru yapmak için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="announcement-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Duyuru Yap</h1>
        <p className="text-gray-600">Telegram kanalınıza özel duyuru gönderin</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="general">Genel Duyuru</TabsTrigger>
          <TabsTrigger value="brand">Marka Duyurusu</TabsTrigger>
        </TabsList>

        {/* GENEL DUYURU */}
        <TabsContent value="general">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form */}
            <div className="space-y-6">
              <Card className="shadow-lg border-0 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <CardTitle>Duyuru Oluştur</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="text">Mesaj Metni</Label>
                    {/* Formatting Toolbar - Mobile Friendly */}
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-100 rounded-lg">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<b>', '</b>')}
                        title="Kalın"
                        className="h-10 w-10 p-0 text-lg font-bold"
                      >
                        B
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<i>', '</i>')}
                        title="İtalik"
                        className="h-10 w-10 p-0 text-lg italic"
                      >
                        I
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<u>', '</u>')}
                        title="Altı Çizili"
                        className="h-10 w-10 p-0 text-lg underline"
                      >
                        U
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<s>', '</s>')}
                        title="Üstü Çizili"
                        className="h-10 w-10 p-0 text-lg line-through"
                      >
                        S
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<code>', '</code>')}
                        title="Kod"
                        className="h-10 w-10 p-0"
                      >
                        <Code className="w-5 h-5" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertFormatting('<a href="URL">', '</a>')}
                        title="Link"
                        className="h-10 w-10 p-0"
                      >
                        <Link2 className="w-5 h-5" />
                      </Button>
                    </div>
                    <Textarea
                      ref={textareaRef}
                      id="text"
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      placeholder="Duyuru mesajınız..."
                      rows={5}
                      data-testid="announcement-text-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="media_upload">Fotoğraf veya Video</Label>
                    {mediaPreview ? (
                      <div className="relative">
                        {mediaType === 'video' ? (
                          <video 
                            src={mediaPreview} 
                            controls
                            className="w-full max-h-96 object-contain rounded-2xl border-2 border-gray-200 bg-gray-100"
                          />
                        ) : (
                          <img 
                            src={mediaPreview} 
                            alt="Preview" 
                            className="w-full max-h-96 object-contain rounded-2xl border-2 border-gray-200 bg-gray-100"
                          />
                        )}
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          className="absolute top-2 right-2"
                          onClick={handleRemoveMedia}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-purple-400 transition-colors cursor-pointer">
                        <input
                          id="media_upload"
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleMediaUpload}
                          className="hidden"
                        />
                        <label htmlFor="media_upload" className="cursor-pointer">
                          <Upload className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">
                            {uploading ? 'Yükleniyor...' : 'Resim veya video yüklemek için tıklayın'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, MP4, MOV</p>
                        </label>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  <CardTitle>Butonlar Ekle</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="custom"
                        checked={buttonType === 'custom'}
                        onChange={(e) => setButtonType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Özel Buton</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="brand"
                        checked={buttonType === 'brand'}
                        onChange={(e) => setButtonType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Marka Butonu</span>
                    </label>
                  </div>

                  {buttonType === 'custom' ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        value={newButton.text}
                        onChange={(e) => setNewButton({ ...newButton, text: e.target.value })}
                        placeholder="Buton metni"
                        data-testid="button-text-input"
                        className="flex-1"
                      />
                      <Input
                        value={newButton.url}
                        onChange={(e) => setNewButton({ ...newButton, url: e.target.value })}
                        placeholder="https://..."
                        data-testid="button-url-input"
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleAddButton}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto"
                        data-testid="add-button"
                      >
                        <Plus className="w-4 h-4 mr-2 sm:mr-0" />
                        <span className="sm:hidden">Ekle</span>
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={newButton.text}
                        onChange={(e) => {
                          const brand = brands.find(b => b.name === e.target.value);
                          setNewButton({ text: brand.name, url: brand.link });
                        }}
                        className="flex-1 h-10 px-3 rounded-2xl border border-gray-300"
                      >
                        <option value="">Marka seçin...</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.name}>{brand.name}</option>
                        ))}
                      </select>
                      <Button 
                        onClick={handleAddBrandButton}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto"
                      >
                        <Plus className="w-4 h-4 mr-2 sm:mr-0" />
                        <span className="sm:hidden">Ekle</span>
                      </Button>
                    </div>
                  )}

                  {formData.buttons.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Mevcut Butonlar:</p>
                      {formData.buttons.map((btn, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-2xl" data-testid={`button-item-${idx}`}>
                          <div>
                            <p className="font-medium text-sm">{btn.text}</p>
                            <p className="text-xs text-gray-600 truncate max-w-xs">{btn.url}</p>
                          </div>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleRemoveButton(idx)}
                            className="hover:bg-red-100 hover:text-red-600"
                            data-testid={`remove-button-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                  <CardTitle>Hedef Kanal Seçimi</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  <p className="text-sm text-gray-600">Duyurunun hangi kanal(lar)a gönderileceğini seçin. Birden fazla kanal seçebilirsiniz.</p>
                  <label className="flex items-center space-x-3 p-3 bg-blue-50 rounded-2xl cursor-pointer hover:bg-blue-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={targetChannels.includes('main')}
                      onChange={() => toggleTargetChannel('main')}
                      className="w-5 h-5 accent-blue-500"
                      data-testid="target-main-channel"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Ana Kanal</p>
                      <p className="text-xs text-gray-600">{teamInfo?.channel_id || 'Ayarlanmamış'}</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-center space-x-3 p-3 rounded-2xl transition-colors ${
                      teamInfo?.info_channel_id
                        ? 'bg-cyan-50 cursor-pointer hover:bg-cyan-100'
                        : 'bg-gray-50 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={targetChannels.includes('info')}
                      onChange={() => toggleTargetChannel('info')}
                      disabled={!teamInfo?.info_channel_id}
                      className="w-5 h-5 accent-cyan-500"
                      data-testid="target-info-channel"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Info Kanal</p>
                      <p className="text-xs text-gray-600">
                        {teamInfo?.info_channel_id || 'Mağaza ayarlarında henüz yapılandırılmadı'}
                      </p>
                    </div>
                  </label>
                </CardContent>
              </Card>

              <Button 
                onClick={handleSendGeneralAnnouncement}
                disabled={sending}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="send-general-announcement-button"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Duyuruyu Gönder
                  </>
                )}
              </Button>
            </div>

            {/* Preview */}
            <div>
              <Card className="shadow-lg border-0 sticky top-8 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                  <CardTitle>Önizleme</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gray-100 rounded-2xl p-4 min-h-64">
                    {formData.photo_url && (
                      <div className="mb-4">
                        {mediaType === 'video' ? (
                          <video 
                            src={formData.photo_url} 
                            controls
                            className="w-full max-h-96 object-contain rounded-2xl bg-white"
                          />
                        ) : (
                          <img 
                            src={formData.photo_url} 
                            alt="Preview" 
                            className="w-full max-h-96 object-contain rounded-2xl bg-white"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>
                    )}
                    {formData.text && (
                      <div 
                        className="text-gray-800 mb-4 whitespace-pre-wrap [&_b]:font-bold [&_i]:italic [&_u]:underline [&_s]:line-through [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:rounded [&_a]:text-blue-600 [&_a]:underline"
                        dangerouslySetInnerHTML={{ 
                          __html: formData.text
                            .replace(/&/g, '&amp;')
                            .replace(/"/g, '&quot;')
                            .replace(/<b>/gi, '###BOLD_OPEN###')
                            .replace(/<\/b>/gi, '###BOLD_CLOSE###')
                            .replace(/<i>/gi, '###ITALIC_OPEN###')
                            .replace(/<\/i>/gi, '###ITALIC_CLOSE###')
                            .replace(/<u>/gi, '###UNDERLINE_OPEN###')
                            .replace(/<\/u>/gi, '###UNDERLINE_CLOSE###')
                            .replace(/<s>/gi, '###STRIKE_OPEN###')
                            .replace(/<\/s>/gi, '###STRIKE_CLOSE###')
                            .replace(/<code>/gi, '###CODE_OPEN###')
                            .replace(/<\/code>/gi, '###CODE_CLOSE###')
                            .replace(/<a href="([^"]*)">/gi, '###LINK_OPEN_$1###')
                            .replace(/<\/a>/gi, '###LINK_CLOSE###')
                            .replace(/</g, '&lt;')
                            .replace(/>/g, '&gt;')
                            .replace(/###BOLD_OPEN###/g, '<b>')
                            .replace(/###BOLD_CLOSE###/g, '</b>')
                            .replace(/###ITALIC_OPEN###/g, '<i>')
                            .replace(/###ITALIC_CLOSE###/g, '</i>')
                            .replace(/###UNDERLINE_OPEN###/g, '<u>')
                            .replace(/###UNDERLINE_CLOSE###/g, '</u>')
                            .replace(/###STRIKE_OPEN###/g, '<s>')
                            .replace(/###STRIKE_CLOSE###/g, '</s>')
                            .replace(/###CODE_OPEN###/g, '<code>')
                            .replace(/###CODE_CLOSE###/g, '</code>')
                            .replace(/###LINK_OPEN_([^#]*)###/g, '<a href="$1" target="_blank">')
                            .replace(/###LINK_CLOSE###/g, '</a>')
                            .replace(/\n/g, '<br/>')
                        }}
                      />
                    )}
                    {formData.buttons.length > 0 && (
                      <div className="space-y-2">
                        {formData.buttons.map((btn, idx) => (
                          <div key={idx} className="bg-blue-500 text-white text-center py-2 px-4 rounded-2xl text-sm font-medium">
                            {btn.text}
                          </div>
                        ))}
                      </div>
                    )}
                    {!formData.text && !formData.photo_url && (
                      <p className="text-gray-400 text-center">Duyurunuz burada görünecek</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* MARKA DUYURUSU */}
        <TabsContent value="brand">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <CardTitle className="flex items-center">
                  <Tag className="w-6 h-6 mr-2" />
                  Marka Duyurusu Oluştur
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="brand">Marka Seçin *</Label>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger data-testid="brand-select">
                      <SelectValue placeholder="Bir marka seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map(brand => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2 p-4 bg-orange-50 rounded-2xl">
                  <Switch
                    id="include_sale"
                    checked={includeSale}
                    onCheckedChange={setIncludeSale}
                    data-testid="include-sale-switch"
                  />
                  <Label htmlFor="include_sale" className="cursor-pointer">
                    <span className="font-semibold text-orange-700">SALE (İndirim) Butonu Ekle</span>
                    <p className="text-sm text-gray-600">Marka için özel indirim duyurusu</p>
                  </Label>
                </div>

                {selectedBrand && (
                  <div className="p-4 bg-purple-50 rounded-2xl">
                    <p className="text-sm font-medium text-gray-700 mb-2">Duyuru Şablonu:</p>
                    <p className="text-xs text-gray-600">
                      {includeSale 
                        ? `🔥 ${brands.find(b => b.id === selectedBrand)?.name} OPPORTUNITIES HAVE STARTED! 🔥\n✨ Unmissable discount opportunities...` 
                        : `🔥 ${brands.find(b => b.id === selectedBrand)?.name} 🔥\n📱 Our Communication Channels...`
                      }
                    </p>
                  </div>
                )}

                <Button 
                  onClick={handleSendBrandAnnouncement}
                  disabled={sending || !selectedBrand}
                  className="w-full h-14 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="send-brand-announcement-button"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Marka Duyurusunu Gönder
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Brand Announcement Preview */}
            {selectedBrand && (
              <Card className="shadow-lg border-0 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                  <CardTitle>Önizleme</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gray-100 rounded-2xl p-4 min-h-64">
                    {brands.find(b => b.id === selectedBrand)?.image_url && (
                      <div className="mb-4">
                        <img 
                          src={brands.find(b => b.id === selectedBrand)?.image_url} 
                          alt="Brand" 
                          className="w-full max-h-96 object-contain rounded-2xl bg-white"
                        />
                      </div>
                    )}
                    <div className="text-gray-800 mb-4 text-sm whitespace-pre-line">
                      {includeSale ? (
                        <div>
                          🔥 <strong>{brands.find(b => b.id === selectedBrand)?.name} OPPORTUNITIES HAVE STARTED!</strong> 🔥
                          <br /><br />
                          ✨ Unmissable discount opportunities on the most trendy {brands.find(b => b.id === selectedBrand)?.name} products are waiting for you.
                          <br /><br />
                          ━━━━━━━<br />
                          📱 <strong>Our Communication Channels:</strong>
                          <br /><br />
                          🟢 <a href="https://wa.me/905541541834" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">WHATSAPP ADMINISTRATOR</a><br />
                          📸 <a href="https://instagram.com/delfieestore" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">INSTAGRAM</a><br />
                          ━━━━━━━
                          <br /><br />
                          👇 <strong>Review & Order Now</strong> 👇
                        </div>
                      ) : (
                        <div>
                          🔥 <strong>{brands.find(b => b.id === selectedBrand)?.name}</strong> 🔥
                          <br /><br />
                          ━━━━━━━<br />
                          📱 <strong>Our Communication Channels:</strong>
                          <br /><br />
                          🟢 <a href="https://wa.me/905541541834" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">WHATSAPP ADMINISTRATOR</a><br />
                          📸 <a href="https://instagram.com/delfieestore" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">INSTAGRAM</a><br />
                          ━━━━━━━
                          <br /><br />
                          👇 <strong>Review & Order Now</strong> 👇
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="bg-blue-500 text-white text-center py-2 px-4 rounded-2xl text-sm font-medium">
                        🔗 {brands.find(b => b.id === selectedBrand)?.name}
                      </div>
                      {includeSale && (
                        <div className="bg-orange-500 text-white text-center py-2 px-4 rounded-2xl text-sm font-medium">
                          🏷️ {brands.find(b => b.id === selectedBrand)?.name} SALE
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
